import { defineComponent as _defineComponent } from 'vue'
import { calcularGanador } from "@/utils/resultados";
import { getConfigDeporte, type Deporte } from "@/utils/deportes-config";
import AsignarNombreEquipo from "./AsignarNombreEquipo.vue";

// ========== HELPERS PARA TERMINOLOGÍA ==========
// Helper: Convertir número de zona a letra (1→A, 2→B, etc.)
import {
  ref,
  computed,
  nextTick,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import {
  generarLiga,
  calcularTablaPosiciones,
  obtenerPartidosDeEquipo,
  obtenerProgresoLiga,
  obtenerEquipoDescansoPorFecha,
  type Liga,
  type PartidoLiga,
  type EstadisticasEquipo,
} from "../../utils/ligas";
import CargarResultado from "./CargarResultado.vue";
import TablaGoleadores from "./TablaGoleadores.vue";
import EditarDetallesPartido from "./EditarDetallesPartido.vue";
import TorneoContextBar from "./TorneoContextBar.vue";
import AsignarJugadores from "./AsignarJugadores.vue";
import ConsumosWrapper from "../Consumos/ConsumosWrapper.vue";
import { showAlert, confirmModal } from "@/utils/toast";
import axios from "@/axios-auth";
import ModalFinalizarCompetencia from "./ModalFinalizarCompetencia.vue";
import {
  obtenerJugadoresOrdenadosLiga,
  puedeFinalizarLiga,
  type JugadorParaFinalizar,
} from "@/utils/rankings";
import { exportarLigaPdf } from "@/utils/exportarLigaPdf";


export default /*#__PURE__*/_defineComponent({
  __name: 'Ligas',
  setup(__props) {

function numeroZonaALetra(numeroZona: number): string {
  return String.fromCharCode(64 + numeroZona); // 65 es 'A'
}

// Helper: Determinar si el deporte usa "Pareja" en lugar de "Equipo"
function usaTerminoPareja(deporte: string): boolean {
  return deporte === "Padel" || deporte === "Tenis";
}
// ================================================

const { proxy } = getCurrentInstance();

const deporte = ref<Deporte>("Futbol");

// Referencias reactivas
const totalEquipos = ref(4);
const liga = ref<Liga | null>(null);
const ligaOriginal = ref<any>(null);
const tab = ref(0);
const fechaExpandida = ref<number | undefined>(undefined);
const equipoSeleccionado = ref<number | null>(null);
const config = ref("");

const guardarInterval = ref();

const modalDetallesVisible = ref(false);
const modalResultadoVisible = ref(false);
const modalAsignarJugadoresVisible = ref(false);
const showCajaLiga = ref(false);
const modalAsignarNombreVisible = ref(false);

const partidoActivo = ref<any>(null);
const equipoActivo = ref<any>(null);

const nombreLiga = ref("");
const categoriaLiga = ref("");
const generoLiga = ref("");
const tipoCompetenciaLiga = ref("");
const estadoLiga = ref("Draft");
const idLiga = ref();
const idInstitucion = ref();
const infoLiga = ref("");
const hayCambiosSinGuardar = ref(false);

// Agregar refs
const modalFinalizarVisible = ref(false);
const jugadoresFinales = ref<JugadorParaFinalizar[]>([]);

const configDeporte = computed(() => getConfigDeporte(deporte.value));
const usaJugadores = computed(() => configDeporte.value.usaJugadores);

// Computed: Término para equipo/pareja según deporte
const terminoEquipo = computed(() => {
  return usaTerminoPareja(deporte.value) ? "Pareja" : "Equipo";
});

const terminoEquipos = computed(() => {
  return usaTerminoPareja(deporte.value) ? "Parejas" : "Equipos";
});

// Headers para las tablas
const headersPartidos = computed(() => [
  { text: "Partido", value: "id", sortable: false, width: "80px" },
  { text: "Local", value: "local", sortable: false, width: "25%" },
  { text: "VS", value: "vs", sortable: false, width: "50px" },
  { text: "Visitante", value: "visitante", sortable: false, width: "25%" },
  { text: "Detalles", value: "detalles", sortable: false },
  { text: "Resultado", value: "resultado", sortable: false },
  { text: "Estado", value: "estado", sortable: false },
  { text: "Acciones", value: "actions", sortable: false },
]);

const headersPartidosEquipo = computed(() => [
  { text: "Fecha", value: "fecha", sortable: true },
  { text: "Rival", value: "rival", sortable: false, width: "25%" },
  { text: "Detalles", value: "detalles", sortable: false },
  { text: "Resultado", value: "resultado", sortable: false },
  { text: "Estado", value: "estado", sortable: false },
  { text: "Acciones", value: "actions", sortable: false },
]);

const headersCronologico = computed(() => [
  { text: "Fecha", value: "detalleFecha", sortable: false, width: "100px" },
  { text: "Hora", value: "hora", sortable: false, width: "70px" },
  { text: "Local", value: "local", sortable: false },
  { text: "VS", value: "vs", sortable: false, width: "40px" },
  { text: "Visitante", value: "visitante", sortable: false },
  { text: "Lugar", value: "lugar", sortable: false },
  { text: "Resultado", value: "resultado", sortable: false },
  { text: "Estado", value: "estado", sortable: false, width: "80px" },
]);

const partidosCronologicos = computed(() => {
  if (!liga.value?.fechas) return [];

  const todos: any[] = [];

  for (const fecha of liga.value.fechas) {
    for (const partido of fecha.partidos) {
      const detFecha = partido.detalles?.fecha || '';
      const detHora = partido.detalles?.hora || '';
      if (!detFecha && !detHora) continue; // Solo incluir los que tienen fecha/hora

      todos.push({
        fechaNum: fecha.numero,
        detalleFecha: detFecha || '—',
        hora: detHora || '—',
        local: partido.local?.nombre || '-',
        localId: partido.local?.id,
        vs: 'vs',
        visitante: partido.visitante?.nombre || '-',
        visitanteId: partido.visitante?.id,
        lugar: partido.detalles?.lugar || '',
        resultado: partido.resultado || '',
        ganadorId: partido.ganador?.id || null,
      });
    }
  }

  // Ordenar por fecha y hora (más viejo primero)
  todos.sort((a, b) => {
    const fechaA = normalizarFechaParaSort(a.detalleFecha === '—' ? '' : a.detalleFecha);
    const fechaB = normalizarFechaParaSort(b.detalleFecha === '—' ? '' : b.detalleFecha);

    if (fechaA !== fechaB) return fechaA.localeCompare(fechaB);

    const horaA = a.hora === '—' ? '99:99' : a.hora;
    const horaB = b.hora === '—' ? '99:99' : b.hora;
    return horaA.localeCompare(horaB);
  });

  return todos;
});

// Computed properties
const tablaPosiciones = computed(() => {
  if (!liga.value) return [];
  return calcularTablaPosiciones(liga.value);
});

const headersPosiciones = computed(() => {
  const esFutbol = deporte.value === 'Futbol';
  const headers: any[] = [
    { text: '#', value: 'posicion', width: '50px' },
    { text: terminoEquipo.value, value: 'nombre' },
    { text: 'PJ', value: 'pj', align: 'center', width: '50px' },
    { text: 'PG', value: 'pg', align: 'center', width: '50px' },
  ];
  if (esFutbol) {
    headers.push({ text: 'PE', value: 'pe', align: 'center', width: '50px' });
  }
  headers.push({ text: 'PP', value: 'pp', align: 'center', width: '50px' });
  if (!esFutbol) {
    headers.push({ text: 'Sets +/-', value: 'setsDiff', align: 'center', width: '70px' });
  }
  headers.push({ text: esFutbol ? 'Goles +/-' : 'Games +/-', value: 'gamesDiff', align: 'center', width: '80px' });
  headers.push({ text: 'Pts', value: 'puntos', align: 'center', width: '50px' });
  return headers;
});

const itemsPosiciones = computed(() => {
  return tablaPosiciones.value.map((fila: any, index: number) => ({
    posicion: index + 1,
    nombre: fila.equipo.nombre,
    pj: fila.pj,
    pg: fila.pg,
    pe: fila.pe,
    pp: fila.pp,
    setsDiff: fila.setsDiff,
    gamesDiff: fila.gamesDiff,
    puntos: fila.puntos,
  }));
});

const progreso = computed(() => {
  if (!liga.value)
    return { partidosJugados: 0, partidosTotales: 0, porcentaje: 0 };
  return obtenerProgresoLiga(liga.value);
});

const partidosDelEquipo = computed(() => {
  if (!liga.value || !equipoSeleccionado.value) return [];
  return obtenerPartidosDeEquipo(liga.value, equipoSeleccionado.value);
});

const equipoDescansaEnFecha = computed(() => {
  if (!liga.value) return new Map();
  return obtenerEquipoDescansoPorFecha(liga.value);
});

// Funciones de generación
function calcularTotalPartidos(): number {
  if (!liga.value) return 0;
  return liga.value.fechas.reduce(
    (total, fecha) => total + fecha.partidos.length,
    0,
  );
}

async function generarLigaClick() {
  if (estadoLiga.value === "Publicado") {
    showAlert({
      icon: "warning",
      title:
        "La liga ya está publicada. Vuelva a ponerla en estado Draft para regenerar.",
      timer: 4000,
      vm: proxy,
    });
    return;
  }

  if (totalEquipos.value < 2 || totalEquipos.value > 50) {
    showAlert({
      icon: "warning",
      title: "El número de equipos debe estar entre 2 y 50.",
      timer: 4000,
      vm: proxy,
    });
    return;
  }

  if (progreso.value.partidosJugados > 0) {
    const result = await confirmModal({
      vm: proxy,
      title: "Liga con resultados",
      text: "Hay resultados cargados. Si regenera la liga, se perderán. ¿Desea continuar?",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
    });

    if (!result.isConfirmed) return;
  }

  try {
    liga.value = generarLiga(totalEquipos.value, deporte.value);

    await guardarCambios();

    showAlert({
      icon: "success",
      title: "Liga generada exitosamente!",
      timer: 2500,
      vm: proxy,
    });

    // Cambiar a la pestaña de fixture
    tab.value = 1;
  } catch (error) {
    showAlert({
      icon: "error",
      title: error.message || "Error al generar la liga",
      timer: 4000,
      vm: proxy,
    });
  }
}

// Funciones de estado
function getEstadoFecha(fecha: any): string {
  const partidosJugados = fecha.partidos.filter((p: any) => p.resultado).length;
  const totalPartidos = fecha.partidos.length;

  if (partidosJugados === 0) return "Pendiente";
  if (partidosJugados === totalPartidos) return "Completa";
  return `${partidosJugados}/${totalPartidos}`;
}

function getColorEstadoFecha(fecha: any): string {
  const partidosJugados = fecha.partidos.filter((p: any) => p.resultado).length;
  const totalPartidos = fecha.partidos.length;

  if (partidosJugados === 0) return "warning";
  if (partidosJugados === totalPartidos) return "success";
  return "info";
}

function tieneDetalles(detalles: any): boolean {
  if (!detalles) return false;
  return !!(
    detalles.fecha ||
    detalles.hora ||
    detalles.lugar ||
    detalles.observaciones
  );
}

function getClaseDiferencia(valor: number): string {
  if (valor > 0) return "success--text font-weight-bold";
  if (valor < 0) return "error--text font-weight-bold";
  return "";
}

function getClaseResultado(partido: any): string {
  if (!partido.ganador) return "";

  const esLocal = partido.local.id === equipoSeleccionado.value;
  const gano = partido.ganador.id === equipoSeleccionado.value;

  return gano ? "ganador-resultado" : "perdedor-resultado";
}

function getColorResultadoEquipo(partido: any): string {
  if (!partido.ganador) return "grey";

  const gano = partido.ganador.id === equipoSeleccionado.value;
  return gano ? "success" : "error";
}

function getEstadoPartidoEquipo(partido: any): string {
  if (!partido.ganador) return "Pendiente";

  const gano = partido.ganador.id === equipoSeleccionado.value;
  return gano ? "Victoria" : "Derrota";
}

function getEstadisticaEquipo(campo: string): number {
  if (!equipoSeleccionado.value || !tablaPosiciones.value) return 0;

  const stats = tablaPosiciones.value.find(
    (s: EstadisticasEquipo) => s.equipo.id === equipoSeleccionado.value,
  );

  return stats ? (stats[campo as keyof EstadisticasEquipo] as number) : 0;
}

async function confirmaFinalizacionTorneo() {
  const result = await confirmModal({
    vm: proxy,
    title: "Finalizar liga.",
    text: "¿Está seguro que desea finalizar la liga? Esta acción no se puede deshacer.",
    icon: "warning",
    showCancelButton: true,
    showCloseButton: true,
  });

  if (!result.isConfirmed) {
    return;
  }

  abrirModalFinalizar();
}

// Funciones de modales
function abrirModalDetalles(partido: any) {
  partidoActivo.value = partido;
  modalDetallesVisible.value = true;
}

function closeModalDetalles() {
  modalDetallesVisible.value = false;
  partidoActivo.value = null;
}

function guardarDetalles(detalles: any) {
  if (!partidoActivo.value) return;

  if (!partidoActivo.value.detalles) {
    partidoActivo.value.detalles = {};
  }

  partidoActivo.value.detalles = {
    ...partidoActivo.value.detalles,
    ...detalles,
  };

  Object.keys(partidoActivo.value.detalles).forEach((key) => {
    if (!partidoActivo.value.detalles[key]) {
      delete partidoActivo.value.detalles[key];
    }
  });

  // Ordenar partidos de la fecha por fecha y hora
  ordenarPartidosDeFecha(partidoActivo.value);

  closeModalDetalles();
  hayCambiosSinGuardar.value = true;
}

/**
 * Ordena los partidos de la fecha que contiene al partido dado,
 * por fecha (detalles.fecha) y hora (detalles.hora).
 * Los partidos sin fecha/hora quedan al final.
 */
function ordenarPartidosDeFecha(partido: any) {
  if (!liga.value?.fechas) return;

  for (const fecha of liga.value.fechas) {
    const idx = fecha.partidos.indexOf(partido);
    if (idx === -1) continue;

    fecha.partidos.sort((a: any, b: any) => {
      const fechaA = normalizarFechaParaSort(a.detalles?.fecha || '');
      const fechaB = normalizarFechaParaSort(b.detalles?.fecha || '');

      if (fechaA !== fechaB) return fechaA.localeCompare(fechaB);

      // Misma fecha: comparar por hora
      const horaA = a.detalles?.hora || '99:99';
      const horaB = b.detalles?.hora || '99:99';
      return horaA.localeCompare(horaB);
    });

    break;
  }
}

/**
 * Normaliza una fecha a formato YYYY-MM-DD para comparación consistente.
 * Soporta: YYYY-MM-DD, DD/MM/YYYY, o cualquier formato parseable por Date.
 */
function normalizarFechaParaSort(fecha: string): string {
  if (!fecha) return '9999-12-31';
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
  const match = fecha.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const d = new Date(fecha);
  if (!isNaN(d.getTime())) return d.toISOString().substring(0, 10);
  return '9999-12-31';
}

function abrirModalResultado(partido: any) {
  if (!partido.local || !partido.visitante) {
    showAlert({
      icon: "warning",
      title: "Este partido no tiene participantes definidos.",
      timer: 4000,
      vm: proxy,
    });
    return;
  }

  partidoActivo.value = partido;
  modalResultadoVisible.value = true;
}

function closeModalResultado() {
  modalResultadoVisible.value = false;
  partidoActivo.value = null;
}

function guardarResultado(modalSets: any, goleadores?: any, esWO?: boolean) {
  if (!liga.value || !partidoActivo.value) return;

  // Marcar el partido como WO si corresponde
  if (esWO) {
    partidoActivo.value.esWO = true;
  } else {
    delete partidoActivo.value.esWO;
  }

  const deporteActual = deporte.value;
  const config = getConfigDeporte(deporteActual);

  // ============================================
  // VALIDACIONES SEGÚN DEPORTE
  // ============================================

  if (deporteActual === "Futbol") {
    // VALIDACIÓN ESPECÍFICA DE FÚTBOL
    if (modalSets.length < 1) {
      showAlert({
        icon: "warning",
        title: "Debe ingresar el resultado del partido.",
        timer: 4000,
        vm: proxy,
      });
      return;
    }

    const goles = modalSets[0];
    if (
      !Number.isInteger(goles.gamesLocal) ||
      !Number.isInteger(goles.gamesVisitante) ||
      goles.gamesLocal < 0 ||
      goles.gamesVisitante < 0
    ) {
      showAlert({
        icon: "warning",
        title: "Debe ingresar la cantidad de goles de ambos equipos.",
        timer: 4000,
        vm: proxy,
      });
      return;
    }

    // VALIDACIÓN OPCIONAL: verificar que la cantidad de goleadores coincida
    // Puedes comentar este bloque si quieres permitir que no se asignen todos los goleadores
    /*
    if (goleadores) {
      const totalGoleadoresLocal = goleadores.local?.length || 0;
      const totalGoleadoresVisitante = goleadores.visitante?.length || 0;

      if (totalGoleadoresLocal !== goles.gamesLocal) {
        showAlert({
          icon: "warning",
          title: `Debe asignar ${goles.gamesLocal} goleador(es) para el equipo local.`,
          timer: 4000,
          vm: proxy,
        });
        return;
      }

      if (totalGoleadoresVisitante !== goles.gamesVisitante) {
        showAlert({
          icon: "warning",
          title: `Debe asignar ${goles.gamesVisitante} goleador(es) para el equipo visitante.`,
          timer: 4000,
          vm: proxy,
        });
        return;
      }
    }
    */
  } else {
    // VALIDACIÓN PARA TENIS/PADEL
    if (modalSets.length < config.minimosResultados) {
      showAlert({
        icon: "warning",
        title: `Se necesitan mínimo ${config.minimosResultados} ${
          config.unidadPrincipal
        }${config.minimosResultados > 1 ? "s" : ""}.`,
        timer: 4000,
        vm: proxy,
      });
      return;
    }

    // Validar valores de sets
    for (const s of modalSets) {
      if (
        !Number.isInteger(s.gamesLocal) ||
        !Number.isInteger(s.gamesVisitante) ||
        s.gamesLocal < 0 ||
        s.gamesVisitante < 0
      ) {
        showAlert({
          icon: "warning",
          title: `Debe completar todos los ${config.unidadSecundaria} con números válidos.`,
          timer: 4000,
          vm: proxy,
        });
        return;
      }
    }
  }

  // ============================================
  // CALCULAR GANADOR Y RESULTADO
  // ============================================
  const { ganador, resultado } = calcularGanador(
    modalSets,
    partidoActivo.value.local,
    partidoActivo.value.visitante,
    deporteActual,
  );

  // ============================================
  // GUARDAR DATOS DEL PARTIDO
  // ============================================

  // Guardar sets (normalizados)
  partidoActivo.value.sets = modalSets.map((s: any) => ({
    gamesLocal: Number(s.gamesLocal),
    gamesVisitante: Number(s.gamesVisitante),
  }));

  // Asignar ganador y resultado
  partidoActivo.value.ganador = ganador;
  partidoActivo.value.resultado = resultado;

  // ============================================
  // GUARDAR GOLEADORES (solo para fútbol)
  // ============================================
  if (deporteActual === "Futbol" && goleadores) {
    partidoActivo.value.goleadores = {
      local: goleadores.local || [],
      visitante: goleadores.visitante || [],
    };
  }

  // ============================================
  // CERRAR MODAL Y LIMPIAR
  // ============================================
  modalResultadoVisible.value = false;
  partidoActivo.value = null;

  // Forzar reactividad
  if (liga.value) {
    liga.value = { ...liga.value };
  }

  // ============================================
  // MOSTRAR CONFIRMACIÓN
  // ============================================
  showAlert({
    icon: "success",
    title: "Resultado guardado correctamente.",
    timer: 2500,
    vm: proxy,
  });
}

// Funciones de equipos
function editarEquipo(equipo: any) {
  if (estadoLiga.value === "Publicado") {
    showAlert({
      icon: "warning",
      title: "La liga ya está publicada. Vuelva a ponerla en estado Draft.",
      timer: 5000,
      vm: proxy,
    });
    return;
  }

  equipoActivo.value = equipo;

  // Determinar qué modal abrir según el deporte
  if (!usaJugadores.value) {
    // FÚTBOL: Solo editar nombre
    modalAsignarNombreVisible.value = true;
  } else {
    // TENIS/PADEL: Asignar jugadores
    modalAsignarJugadoresVisible.value = true;
  }
}

function asignarNombreEquipoLiga(nombre: string) {
  if (!equipoActivo.value) return;

  const equipoId = equipoActivo.value.id;

  // 1. Actualizar en el array de equipos
  equipoActivo.value.nombre = nombre;

  // 2. PROPAGAR A TODOS LOS PARTIDOS
  if (liga.value) {
    liga.value.fechas.forEach((fecha: any) => {
      fecha.partidos.forEach((partido: any) => {
        // Actualizar en local
        if (partido.local && partido.local.id === equipoId) {
          partido.local.nombre = nombre;
          // También actualizar el ganador si corresponde
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombre;
          }
        }

        // Actualizar en visitante
        if (partido.visitante && partido.visitante.id === equipoId) {
          partido.visitante.nombre = nombre;
          // También actualizar el ganador si corresponde
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombre;
          }
        }
      });
    });
  }

  hayCambiosSinGuardar.value = true;
  modalAsignarNombreVisible.value = false;
  equipoActivo.value = null;

  showAlert({
    icon: "success",
    title: `Nombre actualizado: ${nombre}`,
    timer: 2500,
    vm: proxy,
  });
}

async function resetearJugadoresEquipo(equipo: any) {
  // Solo para tenis/padel
  if (!usaJugadores.value) {
    return;
  }

  if (estadoLiga.value === "Publicado") {
    showAlert({
      icon: "warning",
      title: "La liga ya está publicada.",
      timer: 5000,
      vm: proxy,
    });
    return;
  }

  const result = await confirmModal({
    vm: proxy,
    title: "Resetear jugadores",
    text: `¿Está seguro que desea resetear los jugadores del equipo "${equipo.nombre}"?`,
    icon: "warning",
    showCancelButton: true,
    showCloseButton: true,
    confirmButtonText: "Sí, resetear",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return;

  const equipoId = equipo.id;
  const nombrePorDefecto = `Equipo ${equipo.id}`;

  // Resetear
  equipo.nombre = nombrePorDefecto;
  delete equipo.ids;

  // Propagar a todos los partidos (igual que antes)
  if (liga.value && equipoId) {
    liga.value.fechas.forEach((fecha: any) => {
      fecha.partidos.forEach((partido: any) => {
        if (partido.local && partido.local.id === equipoId) {
          partido.local.nombre = nombrePorDefecto;
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombrePorDefecto;
          }
        }

        if (partido.visitante && partido.visitante.id === equipoId) {
          partido.visitante.nombre = nombrePorDefecto;
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombrePorDefecto;
          }
        }
      });
    });
  }

  hayCambiosSinGuardar.value = true;

  showAlert({
    icon: "success",
    title: "Jugadores reseteados correctamente.",
    timer: 2500,
    vm: proxy,
  });
}

function asignarJugadoresAEquipoActivo(jugadores: any) {
  const splitJugador1 = jugadores.jugador1.value.nombreJugador.split(",");
  const nombreJugador1 =
    splitJugador1[0] + " " + splitJugador1[1].trim().substr(0, 1) + ".";
  const idJugador1 = jugadores.jugador1.value.idJugador;

  let nombreJugador2 = "";
  let idJugador2 = null;

  if (tipoCompetenciaLiga.value === "Dobles") {
    const splitJugador2 = jugadores.jugador2.value.nombreJugador.split(",");
    nombreJugador2 =
      splitJugador2[0] + " " + splitJugador2[1].trim().substr(0, 1) + ".";
    idJugador2 = jugadores.jugador2.value.idJugador;
  }

  let nombreEquipo = nombreJugador1;

  if (tipoCompetenciaLiga.value === "Dobles") {
    nombreEquipo += ` - ${nombreJugador2}`;
  }

  const ids = { idJugador1, idJugador2 };

  if (ids.idJugador2 == null) {
    delete ids.idJugador2;
  }

  // Guardar el ID del equipo
  const equipoId = equipoActivo.value?.id;

  // 1. Actualizar el equipo en el array de equipos
  if (equipoActivo.value) {
    equipoActivo.value.nombre = nombreEquipo;
    equipoActivo.value.ids = ids;
  }

  // 2. PROPAGAR A TODOS LOS PARTIDOS (igual que en asignarNombreEquipoLiga)
  if (liga.value && equipoId) {
    liga.value.fechas.forEach((fecha: any) => {
      fecha.partidos.forEach((partido: any) => {
        if (partido.local && partido.local.id === equipoId) {
          partido.local.nombre = nombreEquipo;
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombreEquipo;
          }
        }

        if (partido.visitante && partido.visitante.id === equipoId) {
          partido.visitante.nombre = nombreEquipo;
          if (partido.ganador && partido.ganador.id === equipoId) {
            partido.ganador.nombre = nombreEquipo;
          }
        }
      });
    });
  }

  hayCambiosSinGuardar.value = true;
  modalAsignarJugadoresVisible.value = false;
  equipoActivo.value = null;

  showAlert({
    icon: "success",
    title: "Jugadores asignados correctamente en todos los partidos.",
    timer: 2500,
    vm: proxy,
  });
}

function obtenerIdsJugadoresUsados(equipoExcluido?: any): number[] {
  const idsUsados: number[] = [];

  if (!liga.value) return idsUsados;

  liga.value.equipos.forEach((equipo: any) => {
    // Excluir el equipo que estamos editando
    if (equipoExcluido && equipo.id === equipoExcluido.id) return;

    if (equipo.ids) {
      if (equipo.ids.idJugador1) {
        idsUsados.push(equipo.ids.idJugador1);
      }
      if (equipo.ids.idJugador2) {
        idsUsados.push(equipo.ids.idJugador2);
      }
    }
  });

  return [...new Set(idsUsados)];
}

// Funciones de navegación y guardado
function cambiarEstado(estado: string) {
  estadoLiga.value = estado;
}

async function guardarCambios() {
  if (liga.value) {
    ligaOriginal.value = JSON.parse(JSON.stringify(liga.value));
    hayCambiosSinGuardar.value = false;

    const ligaGuardar = {
      idTorneo: idLiga.value,
      configuraciones: JSON.stringify(liga.value),
      idInstitucion: idInstitucion.value,
    };

    try {
      await axios.post("/Torneos/ModificarConfiguraciones", ligaGuardar);

      showAlert({
        icon: "success",
        title: "Cambios guardados correctamente.",
        timer: 2500,
        vm: proxy,
      });
    } catch (error) {
      showAlert({
        icon: "error",
        title: "Error al guardar los cambios.",
        timer: 4000,
        vm: proxy,
      });
    }
  }
}

async function volver() {
  if (hayCambiosSinGuardar.value) {
    const result = await confirmModal({
      vm: proxy,
      title: "Cambios sin guardar.",
      text: "Posee cambios sin guardar.",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
      cancelButtonText: "Volver",
      confirmButtonText: "Guardar y volver",
    });

    if (result.isConfirmed) {
      await guardarCambios();
    }
  }

  proxy.$router.push("/lista-competiciones");
}

function handleExportarPdf() {
  if (!liga.value) return;
  const store = proxy.$store;
  exportarLigaPdf(liga.value as any, {
    nombre: nombreLiga.value || 'Liga',
    categoria: categoriaLiga.value,
    genero: generoLiga.value,
    deporte: deporte.value,
    institucion: store.state.usuario?.institucion?.nombreInstitucion || '',
    tablaPosiciones: tablaPosiciones.value,
  });
}

// Función para abrir modal
async function abrirModalFinalizar() {
  const validacion = puedeFinalizarLiga(liga.value, progreso.value);

  if (!validacion.puede) {
    showAlert({
      icon: "warning",
      title: "No se puede finalizar",
      text: validacion.razon,
      timer: 4000,
      vm: proxy,
    });
    return;
  }

  jugadoresFinales.value = obtenerJugadoresOrdenadosLiga(
    liga.value,
    tablaPosiciones.value,
    nombreLiga.value,
    categoriaLiga.value,
    generoLiga.value,
  );

  if (jugadoresFinales.value.length === 0) {
    showAlert({
      icon: "warning",
      title: "Sin jugadores asignados",
      text: "No hay jugadores con asignación válida en este torneo.",
      timer: 4000,
      vm: proxy,
    });
    const datosTorneo = {
      nombre: "",
      descripcion: "",
      genero: null,
      categoria: null,
      fecha: "01/01/2025",
      tipoCompetencia: null,
      estado: "Finalizado",
      configuraciones: "",
      idTorneo: idLiga.value,
      idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
    };

    await axios.post("/Torneos/CambiarEstado", datosTorneo);

    await guardarCambios();

    cambiarEstado("Finalizado");
    return;
  }

  modalFinalizarVisible.value = true;
}

// Función para finalizar
async function finalizarTorneo(jugadores: JugadorParaFinalizar[]) {
  try {
    const payload = {
      idTorneo: idLiga.value,
      jugadores: JSON.stringify(
        jugadores.map((j) => ({
          idJugador: j.idJugador,
          nombreJugador: j.nombreJugador,
          posicion: j.ordenMerito,
          puntosAsignados: j.puntosAsignados,
        })),
      ),
      idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
    };

    await axios.post("/Ranking/GuardarRanking", payload);

    const datosTorneo = {
      nombre: "",
      descripcion: "",
      genero: null,
      categoria: null,
      fecha: "01/01/2025",
      tipoCompetencia: null,
      estado: "Finalizado",
      configuraciones: "",
      idTorneo: idLiga.value,
      idInstitucion: proxy.$store.state.usuario.institucion.idInstitucion,
    };

    await axios.post("/Torneos/CambiarEstado", datosTorneo);

    await guardarCambios();

    cambiarEstado("Finalizado");

    modalFinalizarVisible.value = false;

    showAlert({
      icon: "success",
      title: "Liga Finalizada!",
      text: "Los puntos han sido asignados correctamente.",
      timer: 3000,
      vm: proxy,
    });
  } catch (error) {
    showAlert({
      icon: "error",
      title: "Error",
      text: "No se pudo finalizar la liga.",
      timer: 4000,
      vm: proxy,
    });
  }
}

// Lifecycle
onMounted(async () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  const params = {
    idTorneo: proxy.$store.state.idTorneoActual,
  };

  const { data } = await axios.get("/Torneos/GetTorneoById", { params });

  nombreLiga.value = data.nombre;
  categoriaLiga.value = data.categoria;
  generoLiga.value = data.genero;
  tipoCompetenciaLiga.value = data.tipoCompetencia;
  estadoLiga.value = data.estado;
  idLiga.value = data.idTorneo;
  idInstitucion.value = data.idInstitucion;
  deporte.value = data.deporte;

  infoLiga.value = `${nombreLiga.value} - Categoría: ${categoriaLiga.value} - Género: ${generoLiga.value}`;

  if (data.configuraciones) {
    config.value = data.configuraciones;
    const configuraciones = JSON.parse(data.configuraciones);
    liga.value = { ...configuraciones, deporte: data.deporte };
    totalEquipos.value = configuraciones.totalEquipos;
    await nextTick();
  }

  guardarInterval.value = setInterval(() => {
    if (hayCambiosSinGuardar.value && estadoLiga.value !== "Finalizado") {
      guardarCambios();
    }
  }, 30000);
});

onUnmounted(() => {
  clearInterval(guardarInterval.value);
});

watch(
  () => liga.value,
  (nuevaLiga) => {
    if (!ligaOriginal.value) {
      // Guardar copia inicial
      ligaOriginal.value = JSON.parse(JSON.stringify(nuevaLiga));
      hayCambiosSinGuardar.value = false;
      return;
    }

    // Comparar con la copia original
    const haCambiado =
      JSON.stringify(nuevaLiga) !== JSON.stringify(ligaOriginal.value);
    hayCambiosSinGuardar.value = haCambiado;

    console.log("¿Torneo modificado?", haCambiado);
  },
  { deep: true, immediate: true },
);

return { __sfc: true,numeroZonaALetra, usaTerminoPareja, proxy, deporte, totalEquipos, liga, ligaOriginal, tab, fechaExpandida, equipoSeleccionado, config, guardarInterval, modalDetallesVisible, modalResultadoVisible, modalAsignarJugadoresVisible, showCajaLiga, modalAsignarNombreVisible, partidoActivo, equipoActivo, nombreLiga, categoriaLiga, generoLiga, tipoCompetenciaLiga, estadoLiga, idLiga, idInstitucion, infoLiga, hayCambiosSinGuardar, modalFinalizarVisible, jugadoresFinales, configDeporte, usaJugadores, terminoEquipo, terminoEquipos, headersPartidos, headersPartidosEquipo, headersCronologico, partidosCronologicos, tablaPosiciones, headersPosiciones, itemsPosiciones, progreso, partidosDelEquipo, equipoDescansaEnFecha, calcularTotalPartidos, generarLigaClick, getEstadoFecha, getColorEstadoFecha, tieneDetalles, getClaseDiferencia, getClaseResultado, getColorResultadoEquipo, getEstadoPartidoEquipo, getEstadisticaEquipo, confirmaFinalizacionTorneo, abrirModalDetalles, closeModalDetalles, guardarDetalles, ordenarPartidosDeFecha, normalizarFechaParaSort, abrirModalResultado, closeModalResultado, guardarResultado, editarEquipo, asignarNombreEquipoLiga, resetearJugadoresEquipo, asignarJugadoresAEquipoActivo, obtenerIdsJugadoresUsados, cambiarEstado, guardarCambios, volver, handleExportarPdf, abrirModalFinalizar, finalizarTorneo, AsignarNombreEquipo, CargarResultado, TablaGoleadores, EditarDetallesPartido, TorneoContextBar, AsignarJugadores, ConsumosWrapper, ModalFinalizarCompetencia }
}

})